import { useEffect, useState, type FormEvent } from "react";
import { Filter, RotateCcw } from "lucide-react";
import { API_ROLE, API_USER_ROLE, GET_DEPARTMENT_LIST } from "../../../../config/Endpoints.js";
import { Button, ResponsiveDataTable, Select, Spinner, type ResponsiveColumn } from "../../../components/ui/index.ts";
import { useEmployeesLookup } from "../../../components/features/organization-structure/hooks/use-employees-lookup.ts";
import { employeeTypeLabel } from "../../../utils/employee-type.ts";
import { showError } from "../../../utils/toast.ts";
import { EmptyStateMessage } from "../../../components/shared/empty-state-message/empty-state-message.tsx";
import { useRoleManagementQuery } from "../use-role-management-query.ts";

import { roleLabel, type ListResponse, type Role, type UserRole } from "../types.ts";
import styles from "../role-management.module.css";

type UserRoleRow = UserRole & { id: string };
const COLUMNS: ResponsiveColumn<UserRoleRow>[] = [
  { id: "empName", header: "Employee Name", accessor: "empName" },
  { id: "ccEmail", header: "Email", accessor: "ccEmail" },
  { id: "employeeType", header: "Employee Type", render: (row) => employeeTypeLabel(row.employeeType) },
  { id: "department", header: "Department", accessor: "department" },
  { id: "designation", header: "Designation", accessor: "designation" },
  { id: "roles", header: "Roles", render: (row) => row.roles?.length ? <ul className={styles.roleList}>{row.roles.map((role, index) => <li key={`${role}-${index}`}>{role}</li>)}</ul> : "—" },
];
const STATUS_OPTIONS = [
  { value: "VERIFIED", label: "Working" },
  { value: "BLOCKED", label: "Blocked" },
  { value: "LEFTOUT", label: "Left Organization" },
];

export function UsersRoleReportTab() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [employeeId, setEmployeeId] = useState("");
  const [roleId, setRoleId] = useState("");
  const employees = useEmployeesLookup();
  useEffect(() => {
    if (employees.error) showError(employees.error);
  }, [employees.error]);
  const [departmentId, setDepartmentId] = useState("");
  const [status, setStatus] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    employeeId: "",
    departmentId: "",
    status: "",
    roleId: "",
  });
  const departments = useRoleManagementQuery<ListResponse<{ id: number | string; name: string }>>(GET_DEPARTMENT_LIST);
  const roles = useRoleManagementQuery<ListResponse<Role>>(API_ROLE);
  const query = new URLSearchParams({
    pageSize: String(pageSize),
    currentPage: String(page),
    deptId: appliedFilters.departmentId,
    status: appliedFilters.status,
    employeeId: appliedFilters.employeeId,
    roleId: appliedFilters.roleId,
  });
  const { data, loading, error } = useRoleManagementQuery<ListResponse<UserRole>>(`${API_USER_ROLE}?${query}`);
  const rows: UserRoleRow[] = (data?.data ?? []).map((row, index) => ({ ...row, id: String(row.id ?? `${page}-${index}`) }));

  const applyFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAppliedFilters({ employeeId, departmentId, status, roleId }); setPage(0);
  };

  const clearFilters = () => {
    setEmployeeId("");
    setDepartmentId("");
    setStatus("");
    setRoleId("");
    setAppliedFilters({
      employeeId: "",
      departmentId: "",
      status: "",
      roleId: "",
    });
    setPage(0);
  };

  return (
    <div>
      <form className={styles.toolbar} onSubmit={applyFilters}>
        <div className={styles.field}>
          <Select
            id="user-role-search"
            label="Employee Name"
            placeholder="Select Employee Name"
            searchable
            isClearable
            options={employees.employees}
            disabled={employees.loading}
            value={employeeId}
            onChange={setEmployeeId}
          />
        </div>
        <div className={styles.field}>
          <Select
            id="user-role-department"
            label="Department"
            searchable
            options={(departments.data?.data ?? []).map((department) => ({
              value: String(department.id),
              label: department.name,
            }))}
            placeholder="Select Department"
            value={departmentId}
            onChange={setDepartmentId}
            disabled={departments.loading}
          />
        </div>
        <div className={styles.field}>
          <Select
            id="user-role-role"
            label="Role"
            placeholder="Select Role"
            searchable
            isClearable
            options={(roles.data?.data ?? []).map((role) => ({
              value: String(role.roleId),
              label: roleLabel(role),
            }))}
            value={roleId}
            onChange={(value) => setRoleId(value)}
            disabled={roles.loading}
          />
        </div>
        <div className={styles.field}>
          <Select
            id="user-role-status"
            label="Status"
            options={STATUS_OPTIONS}
            placeholder="Select Status"
            value={status}
            onChange={setStatus}
          />
        </div>
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="accent"
            startIcon={<Filter size={16} />}
            disabled={loading}
          >
            Apply Filter
          </Button>
          <Button
            variant="light-grey"
            startIcon={<RotateCcw size={16} />}
            onClick={clearFilters}
          >
            Clear Filter
          </Button>
        </div>
      </form>
      {error ? null : loading ? <Spinner /> : rows.length === 0 ? (
        <EmptyStateMessage message="No record found" />
      ) : (
        <>

          <ResponsiveDataTable<UserRoleRow>
            aria-label="Users role report"
            serialNumber
            columns={COLUMNS}
            data={rows}
            pagination
            page={page + 1}
            pageSize={pageSize}
            totalItems={data?.totalRequest ?? 0}
            onPageChange={(nextPage) => setPage(nextPage - 1)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            pageSizeOptions={[5, 10, 20, 30, 40, 50]}
          />
        </>
      )}
    </div>
  );
}
